import type { AudienceTag } from '@digital-twins/shared-types'

const STEM_SEPARATOR = /:\s+-\s+|\s+-\s+|:\s+| – |\.[ ]?– /
const AFFIRMATIVE = new Set(['1', 'selected', 'yes', 'true'])
const NEGATIVE = new Set(['0', 'not selected', 'no', 'false'])

function splitStemAndOption(col: string): { stem: string; option: string } | null {
  const match = STEM_SEPARATOR.exec(col)
  if (!match || match.index === undefined) return null
  const stem = col.slice(0, match.index).trim()
  const option = col.slice(match.index + match[0].length).trim()
  if (stem.length > 0 && option.length > 0) return { stem, option }
  return null
}

function findParentKey(
  attributes: Record<string, unknown>,
  stem: string,
): unknown | undefined {
  const direct = attributes[stem]
  if (direct !== null && direct !== undefined) return direct

  const stemQ = stem.endsWith('?') ? stem : stem + '?'
  const withQ = attributes[stemQ]
  if (withQ !== null && withQ !== undefined) return withQ

  const stemNoQ = stem.endsWith('?') ? stem.slice(0, -1) : stem
  const withoutQ = attributes[stemNoQ]
  if (withoutQ !== null && withoutQ !== undefined) return withoutQ

  const lowerStem = stem.toLowerCase()
  for (const key of Object.keys(attributes)) {
    if (key.toLowerCase() === lowerStem) return attributes[key]
  }

  return undefined
}

function matchCondition(
  attributes: Record<string, unknown>,
  cond: { sourceColumn: string; values: Array<string> },
): boolean {
  const raw = attributes[cond.sourceColumn]
  if (raw !== null && raw !== undefined) {
    const personaValues = Array.isArray(raw)
      ? (raw as Array<unknown>).map((v) => String(v).trim().toLowerCase())
      : [String(raw).trim().toLowerCase()]
    const allowed = new Set(cond.values.map((v) => v.trim().toLowerCase()))
    return personaValues.some((pv) => allowed.has(pv))
  }

  const split = splitStemAndOption(cond.sourceColumn)
  if (!split) return false

  const parentVal = findParentKey(attributes, split.stem)
  if (parentVal === undefined) return false

  return matchMemberPresence(parentVal, split.option, cond.values)
}

function matchMemberPresence(
  parentVal: unknown,
  memberOption: string,
  condValues: Array<string>,
): boolean {
  const parentValues = Array.isArray(parentVal)
    ? (parentVal as Array<unknown>).map((v) => String(v).trim().toLowerCase())
    : [String(parentVal).trim().toLowerCase()]

  const isPresent = parentValues.includes(memberOption.toLowerCase())
  const condVals = condValues.map((v) => v.trim().toLowerCase())
  const wantsSelected = condVals.some((v) => AFFIRMATIVE.has(v))
  const wantsNotSelected = condVals.some((v) => NEGATIVE.has(v))

  if (isPresent && wantsSelected) return true
  if (!isPresent && wantsNotSelected) return true
  return false
}

export function getMatchingTagNames(
  attributes: Record<string, unknown>,
  audienceTags: Array<AudienceTag> | null | undefined,
): Array<string> {
  if (!audienceTags || audienceTags.length === 0) return []

  const matched: Array<string> = []

  for (const tag of audienceTags) {
    if (!tag.conditions || tag.conditions.length === 0) continue

    const allConditionsMet = tag.conditions.every((cond) =>
      matchCondition(attributes, cond),
    )

    if (allConditionsMet) {
      matched.push(tag.name)
    }
  }

  return matched
}
